//src/Pages/Login/Login.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import bgImage from "../../assest/img/NlimLoginImg.jpg";
// import logo from "../../assest/img/fmld-logo.png";
import LoginLogo from "../../assest/svg/NigeriaCoatofArm.svg";
import { useAuth } from "../../context/AuthContext";
import { getLandingRouteForRole, hasUIAccess } from "../../config/rbacConfig";

export default function Login() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // If a valid session already exists (e.g. page refresh or back-nav), skip
  // the login form and go straight to the dashboard.
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    const roleId = auth.user?.roleId;
    if (!hasUIAccess(roleId)) {
      void auth.logout();
      const msg = roleId === 'R07'
        ? 'This account is configured for API integration only and cannot access the web interface.'
        : 'No role has been assigned to your account. Please contact your system administrator.';
      toast.error(msg, { position: 'top-right', autoClose: 6000 });
      return;
    }
    navigate(getLandingRouteForRole(auth.user), { replace: true });
  }, [auth, auth.isAuthenticated, auth.user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const nextSession = await auth.login({ email, password });
      const roleId = nextSession?.user?.roleId;

      if (!hasUIAccess(roleId)) {
        await auth.logout();
        const msg = roleId === 'R07'
          ? 'This account is configured for API integration only and cannot access the web interface.'
          : 'No role has been assigned to your account. Please contact your system administrator to assign the appropriate role.';
        toast.error(msg, { position: 'top-right', autoClose: 6000 });
        return;
      }
      toast.success('Welcome to your Dashboard', {
        position: 'top-right',
        autoClose: 4000,
        icon: '👋',
      });
      navigate(getLandingRouteForRole(nextSession?.user), {
        state: { fromLogin: true },
        replace: true,
      });
    } catch (error) {
      const status = error?.response?.status;
      const msg =
        status === 401 ? "Invalid email or password." :
        status === 422 ? "Please check your email format." :
        !status        ? "Cannot reach server. Check your connection." :
        `Login failed (${status}). Try again.`;
      toast.error(msg, { position: "top-right" });
    }
  };



  return (
    <div className="relative w-screen h-screen flex items-center justify-center overflow-hidden font-sans">

      {/* ── Background image ── */}
      <div
        className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bgImage}) ` }}
      />

      {/* ── Dark gradient overlay ── */}
      <div
        className="absolute inset-0 z-10"
        style={{
          background:
            "linear-gradient(90deg, rgba(0,0,0,0.50) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.15) 100%)",
        }}
      />

      {/* ══════════════════════════════════════════════
          CONTENT WRAPPER — full width, two columns
      ══════════════════════════════════════════════ */}
      <div className="relative z-20 w-full h-full flex items-center px-6 sm:px-10 lg:px-16 xl:px-24 gap-6 lg:gap-10">

        {/* ── LEFT PANE — hidden on mobile ── */}
        <div className="hidden md:flex flex-col gap-4 lg:gap-5 flex-1 min-w-0">
          <h1 className="text-white font-radley leading-tight tracking-tight text-3xl lg:text-4xl xl:text-5xl m-0">
            FMLD&nbsp;— NLIMS
          </h1>
          <p className="text-white/90 font-radley leading-relaxed text-md lg:text-base m-0 max-w-[420px]">
            The authoritative source of truth for the
            <br />
            Nigerian Livestock Information.
          </p>
          <button
            type="button"
            onClick={() => navigate('/public')}
            className="font-radley w-fit inline-flex items-center justify-center px-7 py-2.5 rounded-full border-2 border-white bg-white text-[#2d6a2d] font-semibold text-sm tracking-wide cursor-pointer transition-all duration-200 hover:bg-transparent hover:text-white"
          >
            Learn More
          </button>
        </div>

        {/* ── RIGHT PANE — Login card ── */}
        <div className="flex items-center justify-center w-full md:w-auto md:flex-none font-radley">
          <div
            className="w-full md:w-[480px] lg:w-[520px] xl:w-[560px] flex flex-col rounded-2xl border border-white/10 px-8 sm:px-10 lg:px-12 pt-8 pb-10"
            style={{
              background: "rgba(46, 48, 45, 0.6)",
              backdropFilter: "blur(22px)",
              WebkitBackdropFilter: "blur(22px)",
              boxShadow:
                "0 32px 80px rgba(0,0,0,0.55), 0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)",
            }}
          >
            {/* ── Logo pill ── */}
            <div className="flex items-center justify-center gap-2.5 mb-3 border border-white/20 rounded-full py-1 px-1 bg-white/10 w-max mx-auto">
              {/*
                Replace SVG with real logo:
                <img src={logo} alt="FMLD" className="w-8 h-8 rounded-full object-cover" />
              */}
              <div className="shrink-0 w-8 h-8 rounded-full bg-[#1a4d1a] flex items-center justify-center overflow-hidden">
                <svg viewBox="0 0 40 40" width="32" height="32">

                  <image href={LoginLogo} x="8" y="8" width="24" height="24" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-white/80 text-[0.55rem] font-semibold tracking-[0.1em] uppercase leading-tight">
                  Federal Ministry of
                </span>
                <span className="text-white/80 text-[0.55rem] font-semibold tracking-[0.1em] uppercase leading-tight">
                  Livestock Development
                </span>
              </div>
            </div>

            {/* ── System name ── */}
            <p className="text-white font-semibold text-lg text-center leading-snug mb-5">
              National Livestock Information
              <br />
              Management System (NLIMS)
            </p>

            {/* ── Welcome heading ── */}
            <h2 className="text-white  text-center text-2xl lg:text-4xl tracking-tight mb-1.5">
              Welcome to NLIMS
            </h2>
            <p className="text-white/55 text-lg text-center mb-7">
              Enter Your Details
            </p>

            {/* ── Form ── */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">

              {/* Email */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-white/85 text-sm font-medium">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full px-5 py-3.5 rounded-full border border-white/20 bg-white/10 text-white text-sm placeholder:text-white/40 outline-none transition-all duration-200 focus:border-white/50 focus:bg-white/15 caret-green-400"
                />
              </div>

              {/* Password */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="password" className="text-white/85 text-sm font-medium">
                  Password
                </label>
                <div className="relative w-full">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="w-full px-5 py-3.5 pr-12 rounded-full border border-white/20 bg-white/10 text-white text-sm placeholder:text-white/40 outline-none transition-all duration-200 focus:border-white/50 focus:bg-white/15 caret-green-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-1 bg-transparent border-none cursor-pointer text-white/50 hover:text-white/85 transition-colors duration-150"
                  >
                    {showPassword ? (
                      <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : (
                      <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Forgot password */}
              <div className="flex justify-end">
                <button
                  type="button"
                  className="text-white/75 text-xs font-semibold underline underline-offset-2 cursor-pointer bg-transparent border-none p-0 hover:text-white transition-colors duration-150"
                >
                  Forgot Password?
                </button>
              </div>

              {/* Continue */}
              <button
                type="submit"
                disabled={auth.loading}
                className="w-full py-4 mt-1 rounded-full border-none bg-[#28BA12] text-white font-bold text-base tracking-wide cursor-pointer shadow-[0_4px_20px_rgba(76,175,80,0.45)] transition-all duration-150 hover:bg-green-400 hover:shadow-[0_6px_28px_rgba(76,175,80,0.60)] active:scale-[0.98]"              >
                {auth.loading ? "Signing in..." : "Continue"}
              </button>
            </form>
          </div>
        </div>

      </div>{/* end content wrapper */}
    </div>
  );
}
